/* eslint-disable react/prop-types */
import { Box, Stack, Typography } from '@mui/material'

import { CustomTooltip } from '@hi/brabus'

import { renderChip, renderQuality } from 'utils/chip'
import {
  MESSAGE_LIMIT_VALUES,
  MESSAGE_LIMIT_DESCRIPTION,
  NUMBER_QUALITY_VALUES,
  NUMBER_STATUS_VALUES,
} from 'constants/index'
import { Info } from '@mui/icons-material'
import getFormatedPhoneNumber from 'utils/formatPhoneNumber'
import Grid from '../Grid'

/**
 *
 * @param {object} props
 * @param {Array} props.rows - Array of rows to be displayed in the data grid
 * @param {number} props.total - Total of record
 * @param {boolean} props.loading - loading of data
 * @param {boolean} props.isPreviousData - Previous data
 * @returns JSX.Element
 * @description CustomDataGrid component renders a data grid with custom columns and rows.
 * It uses the Grid component from the MUI library and customizes the columns and rows based on the provided data.
 * The component also uses the useGetQualityNumberList and useQualityNumberFilters hooks to fetch data and manage filters.
 * The columns include phone number, account name, status, quality, rate limit, and WABA.
 * Each column has a custom header and cell rendering function to display the data in a specific format.
 */
function CustomDataGrid({ rows, total, loading, isPreviousData }) {
  const columns = [
    {
      field: 'phoneNumber',
      sortable: false,
      flex: 1,
      minWidth: 100,
      renderHeader: () => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'none',
            gap: '0.25rem',
          }}
        >
          <Typography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
            Número do telefone
          </Typography>
        </Box>
      ),
      renderCell: ({ row }) => {
        return (
          <Stack flexDirection="column" sx={{ width: '100%' }}>
            <CustomTooltip title={row.phoneNumber}>
              <Typography
                variant="body1"
                sx={{
                  textWrap: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {getFormatedPhoneNumber(row.phoneNumber)}
              </Typography>
            </CustomTooltip>
          </Stack>
        )
      },
    },
    {
      field: 'verifiedName',
      sortable: false,
      flex: 1,
      minWidth: 100,
      renderHeader: () => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'none',
            gap: '0.25rem',
          }}
        >
          <Typography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
            Nome visível no WhatsApp
          </Typography>
        </Box>
      ),
      renderCell: ({ row }) => {
        return (
          <Stack flexDirection="column" sx={{ width: '100%' }}>
            <CustomTooltip title={row.verifiedName || 'Indisponível'}>
              <Typography
                variant="body1"
                sx={{
                  textWrap: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {row.verifiedName || 'Indisponível'}
              </Typography>
            </CustomTooltip>
          </Stack>
        )
      },
    },
    {
      field: 'operationalStatus',
      sortable: true,
      flex: 1,
      minWidth: 100,
      renderHeader: () => (
        <CustomTooltip
          title={
            <Stack p={1}>
              <Typography variant="inherit" fontWeight="bold">
                Mostra a disponibilidade do número diante do limite de mensagens, nível de qualidade e situação da conta
                associadas a ele.
              </Typography>
            </Stack>
          }
          bordercolor="#DADDE9"
          bgcolor="#F5F5F9"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <Typography variant="inherit" fontWeight="bold">
              Status
            </Typography>
            <Info
              fontSize="small"
              style={{
                color: '#666',
              }}
            />
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ row }) => (
        <Box flex={1} display="flex" alignItems="center">
          {renderChip(NUMBER_STATUS_VALUES[row.operationalStatus])}
        </Box>
      ),
    },
    {
      field: 'qualityRating',
      sortable: true,
      flex: 1,
      minWidth: 100,
      renderHeader: () => (
        <CustomTooltip
          title={
            <Stack p={1}>
              <Typography variant="inherit" fontWeight="bold">
                Baseia-se nas mensagens enviadas nos últimos sete dias e o feedback dos seus clientes em relação a elas.
              </Typography>
            </Stack>
          }
          bordercolor="#DADDE9"
          bgcolor="#F5F5F9"
          placement="top"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <Typography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Nível de qualidade
            </Typography>
            <Info
              fontSize="small"
              style={{
                color: '#666',
              }}
            />
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ row }) => (
        <Box flex={1} display="flex" alignItems="center">
          {renderQuality(NUMBER_QUALITY_VALUES[row.qualityRating])}
        </Box>
      ),
    },
    {
      field: 'messageLimit',
      sortable: false,
      flex: 1,
      minWidth: 100,
      renderHeader: () => (
        <CustomTooltip
          title={
            <Stack p={1}>
              <Typography variant="inherit" fontWeight="bold">
                Identifica a quantidade máxima de mensagens ativas que este número está habilitado para enviar dentro de
                24 horas.
              </Typography>
            </Stack>
          }
          bordercolor="#DADDE9"
          bgcolor="#F5F5F9"
          placement="top"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <Typography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Limite de mensagens
            </Typography>
            <Info
              fontSize="small"
              style={{
                color: '#666',
              }}
            />
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ row }) => {
        return (
          <Stack flexDirection="column" sx={{ width: '100%' }}>
            <CustomTooltip title={MESSAGE_LIMIT_DESCRIPTION[MESSAGE_LIMIT_VALUES[row.messageLimit]]}>
              <Typography
                variant="body1"
                sx={{
                  textWrap: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {MESSAGE_LIMIT_DESCRIPTION[MESSAGE_LIMIT_VALUES[row.messageLimit]]}
              </Typography>
            </CustomTooltip>
          </Stack>
        )
      },
    },
    {
      field: 'wabaName',
      sortable: false,
      flex: 1,
      minWidth: 100,
      renderHeader: () => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'none',
            gap: '0.25rem',
          }}
        >
          <Typography variant="inherit" fontWeight="bold">
            WABA
          </Typography>
        </Box>
      ),
      renderCell: ({ row }) => {
        return (
          <Stack flexDirection="column" sx={{ width: '100%' }}>
            <CustomTooltip title={row.wabaName || 'Indisponível'}>
              <Typography
                variant="body1"
                sx={{
                  textWrap: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {row.wabaName || 'Indisponível'}
              </Typography>
            </CustomTooltip>
          </Stack>
        )
      },
    },
  ]

  return (
    <Box
      display="flex"
      mt="1rem"
      flexDirection="column"
      gap="1.5rem"
      sx={{ borderRadius: '4px', masHeight: '50rem', border: '1px solid #BDBDBD', padding: '1.5rem 1rem' }}
    >
      <Typography
        sx={{
          fontSize: '1.5rem',
          fontWeight: 700,
          lineHeight: '1.5rem',
        }}
      >
        Números cadastrados
      </Typography>
      <Grid
        columns={columns}
        loading={loading}
        rows={rows}
        getRowId={(el) => el.id}
        rowCount={total || 0}
        pageSizeOptions={[10, 25, 50, 100]}
        isPreviousData={isPreviousData}
      />
    </Box>
  )
}

export default CustomDataGrid
